import { roundUsd } from "./manufacturerPayablesMath.js";

export const QB_BOOKS_EVENT_KINDS = ["purchases", "manual"] as const;
export type QbBooksEventKind = (typeof QB_BOOKS_EVENT_KINDS)[number];

export const QB_BOOKS_EVENT_STATUSES = ["posted", "void"] as const;
export type QbBooksEventStatus = (typeof QB_BOOKS_EVENT_STATUSES)[number];

export const QB_BOOKS_COMMITTED_PO_STATUSES = [
  "issued",
  "confirmed",
  "partially_received",
  "received",
] as const;

export type QbBooksPoLine = {
  po_number: string;
  po_date: string;
  vendor_name: string;
  status: string;
  total_usd: number;
};

export type QbBooksVendorRollup = {
  vendor_name: string;
  po_count: number;
  total_usd: number;
};

export type QbBooksReport = {
  period_start: string;
  period_end: string;
  po_count: number;
  total_usd: number;
  by_vendor: QbBooksVendorRollup[];
  lines: QbBooksPoLine[];
};

export function isQbBooksEventKind(v: string): v is QbBooksEventKind {
  return (QB_BOOKS_EVENT_KINDS as readonly string[]).includes(v);
}

export function isQbBooksEventStatus(v: string): v is QbBooksEventStatus {
  return (QB_BOOKS_EVENT_STATUSES as readonly string[]).includes(v);
}

export function vendorLabel(name: string): string {
  const t = name.trim();
  return t || "(no vendor)";
}

export function aggregatePurchaseOrders(
  lines: readonly QbBooksPoLine[],
  period_start: string,
  period_end: string
): QbBooksReport {
  const by = new Map<string, QbBooksVendorRollup>();
  let total_usd = 0;
  for (const line of lines) {
    const vendor_name = vendorLabel(line.vendor_name);
    const amt = roundUsd(line.total_usd);
    total_usd = roundUsd(total_usd + amt);
    const row = by.get(vendor_name) ?? { vendor_name, po_count: 0, total_usd: 0 };
    row.po_count += 1;
    row.total_usd = roundUsd(row.total_usd + amt);
    by.set(vendor_name, row);
  }
  return {
    period_start,
    period_end,
    po_count: lines.length,
    total_usd,
    by_vendor: [...by.values()].sort((a, b) => b.total_usd - a.total_usd || a.vendor_name.localeCompare(b.vendor_name)),
    lines: [...lines],
  };
}

/** What hit QuickBooks minus what the app report said. Zero means they matched. */
export function qbBooksVarianceUsd(appAmountUsd: number, qbAmountUsd: number): number {
  return roundUsd(qbAmountUsd - appAmountUsd);
}

export function qbBooksReportCsv(report: QbBooksReport): string {
  const rows = [
    ["Period start", report.period_start],
    ["Period end", report.period_end],
    ["PO count", String(report.po_count)],
    ["App total USD", report.total_usd.toFixed(2)],
    [],
    ["Vendor", "PO count", "Total USD"],
    ...report.by_vendor.map((v) => [v.vendor_name, String(v.po_count), v.total_usd.toFixed(2)]),
  ];
  return rows.map((r) => r.map(csvCell).join(",")).join("\n");
}

function csvCell(value: string): string {
  if (/[",\n]/.test(value)) return `"${value.replace(/"/g, '""')}"`;
  return value;
}
